import { CameraControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import debounce from 'lodash.debounce';
import { useEffect, useRef } from 'react';
import { Vector3 } from 'three';
import { useSnapshot } from 'valtio';

import {
  DEFAULT_CAMERA_POSITION,
  DEFAULT_THUMBNAIL_POSITION,
} from 'components/AnimationPlayer/components/SceneSetup';
import { sceneSettingsStore } from 'stores';
import { AnimationPlayerStoreInstance } from 'stores/animationPlayerStoreFactory';
import { USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME } from 'stores/sceneSettingsStore';

export default function DefaultCameraControls({
  animationPlayerStore,
}: {
  animationPlayerStore: AnimationPlayerStoreInstance;
}) {
  const {
    cameraSettings: { serializedCamera },
  } = useSnapshot(sceneSettingsStore.state, { sync: true });
  const { camera, controls } = useThree();
  const cameraControls = controls as unknown as CameraControls;

  const savedCameraString = useRef<string>('');

  // Hand over the camera controls to the animation player store
  useEffect(() => {
    if (cameraControls) {
      animationPlayerStore.setCameraControl(cameraControls);
    }
  }, [cameraControls, animationPlayerStore]);

  // Set the initial camera position
  useEffect(() => {
    if (cameraControls) {
      const params = new URLSearchParams(window.location.search);
      const hasThumbCameraParam =
        params.get(USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME) === 'true';

      if (hasThumbCameraParam) {
        // Case 1: Thumbnail camera
        cameraControls.setLookAt(
          DEFAULT_THUMBNAIL_POSITION[0],
          DEFAULT_THUMBNAIL_POSITION[1],
          DEFAULT_THUMBNAIL_POSITION[2],
          0,
          0.75,
          0,
        );
      } else if (serializedCamera) {
        if (serializedCamera !== savedCameraString.current) {
          // Case 2: Serialized camera from scene settings (we skip the one that was just saved)
          const cameraDetails = JSON.parse(serializedCamera);
          cameraControls.setLookAt(
            cameraDetails.positionX,
            cameraDetails.positionY,
            cameraDetails.positionZ,
            cameraDetails.targetX,
            cameraDetails.targetY,
            cameraDetails.targetZ,
          );
        }
      } else {
        // Case 3: Default camera
        cameraControls.setLookAt(
          DEFAULT_CAMERA_POSITION[0],
          DEFAULT_CAMERA_POSITION[1],
          DEFAULT_CAMERA_POSITION[2],
          0,
          0.5,
          0,
        );
        // Saving the camera state will enable reset to restore the default camera
        cameraControls.saveState();
      }
    }
  }, [serializedCamera, cameraControls]);

  // Update scene settings when camera has been moved
  useEffect(() => {
    if (!cameraControls) return;

    const updateCameraDebounce = debounce(
      () => {
        const currentTarget = new Vector3();
        cameraControls.getTarget(currentTarget);
        const cameraProperties = {
          positionX: camera.position.x,
          positionY: camera.position.y,
          positionZ: camera.position.z,
          targetX: currentTarget.x,
          targetY: currentTarget.y,
          targetZ: currentTarget.z,
        };
        savedCameraString.current = JSON.stringify(cameraProperties);
        sceneSettingsStore.set(
          'cameraSettings',
          'serializedCamera',
          JSON.stringify(cameraProperties),
        );
      },
      300,
      {
        leading: true,
      },
    );

    cameraControls.addEventListener('rest', updateCameraDebounce);

    return () =>
      cameraControls.removeEventListener('rest', updateCameraDebounce);
  }, [camera, cameraControls]);

  return <CameraControls makeDefault />;
}
